import React, { useContext } from 'react';
import { Formik, Form, Field } from 'formik';
import { Button, Card, Col, Row } from 'reactstrap';
import * as Yup from 'yup'; // For validation
import { FaCheck } from 'react-icons/fa6';
import FormikInputFieldV2 from 'components/Fields/FormikInputFieldV2';
import LoadingButton from 'components/common/LoadingButton';
import { axiosPost, axiosUpdate } from 'services';
import { authContext } from 'contexts/AuthContext';
import { toast } from 'react-toastify';
import CardWithTitle from 'components/CardWithTitle';
import CardWithTitleAndButton from 'components/CardWithTitleAndButton';

// Validation schema
const RateManagementSchema = Yup.object().shape({
  fromPin: Yup.number()
    .required('From PIN code is required.')
    .min(110001, 'PIN code cannot be less than 110001.')
    .max(999999, 'PIN code cannot be more than 999999.'),
  toPin: Yup.number()
    .required('To PIN code is required.')
    .min(110001, 'PIN code cannot be less than 110001.')
    .max(999999, 'PIN code cannot be more than 999999.'),
  providedBy: Yup.string().required('Provided by is required.'),
  vehicleType: Yup.string().required('Vehicle Type is required.'),
  deliveryRate: Yup.number()
    .required('Delivery Rate is required.')
    .positive('Rate must be positive number.')
    .max(999, 'Rate cannot be more than 999.'),
  validity: Yup.date()
    .required('Validity is required.')
    .min(new Date(), 'Validity should be aleast one day.'),
});

const visibilityToggle = ({ field, form }) => {
  return (
    <div className="toggle-check cmn-toggle d-flex justify-content-center">
      <div className="d-inline-block position-relative">
        <input
          type="checkbox"
          onChange={() => form.setFieldValue(field.name, !field.value)}
          checked={field.value}
          id={field.name}
        />
        <FaCheck size={10} />
      </div>
    </div>
  );
};

// The parent of this component will call the API for the initial markup percentage and visibility state.
const CreateRate = ({
  onDiscard,
  defaultValues,
  onCreateRate,
  disablePinInputs = false,
}) => {
  const { auth } = useContext(authContext);

  const initialValues = defaultValues ?? {
    fromPin: undefined,
    toPin: undefined,
    providedBy: undefined,
    vehicleType: undefined,
    deliveryRate: undefined,
    validity: undefined,
    status: true,
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);

      const data = {
        from: String(values.fromPin),
        to: String(values.toPin),
        providedBy: values.providedBy,
        rate: Number(values.deliveryRate),
        validityTill: new Date(values.validity).toISOString(),
        vehicleType: values.vehicleType,
        status: values.status ? 'ACTIVE' : 'INACTIVE',
        id: defaultValues ? defaultValues.id : undefined,
        isRequestedOne: defaultValues?.isRequestedOne
          ? defaultValues?.isRequestedOne
          : false,
      };

      await onCreateRate(data);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setSubmitting(false);
      onDiscard();
    }
  };

  return (
    <Formik
      validationSchema={RateManagementSchema}
      onSubmit={handleSubmit}
      initialValues={initialValues}
      children={({ isValid, dirty, isSubmitting, values }) => (
        <Form className="create-rate-container">
          <CardWithTitleAndButton>
            <Row>
              <Col sm={6}>
                <Field
                  required
                  id="idFromPin"
                  name="fromPin"
                  label="From PIN"
                  type="number"
                  component={FormikInputFieldV2}
                  placeholder="Enter From PIN"
                  disabled={disablePinInputs}
                />
              </Col>
              <Col sm={6}>
                <Field
                  required
                  id="toPin"
                  name="toPin"
                  label="To PIN"
                  type="number"
                  component={FormikInputFieldV2}
                  placeholder="Enter To PIN"
                  disabled={disablePinInputs}
                />
              </Col>
            </Row>
            <Row>
              <Col sm={6}>
                <Field
                  required
                  id="providedBy"
                  name="providedBy"
                  label="Provided By"
                  type="text"
                  component={FormikInputFieldV2}
                  placeholder="Enter Provided By"
                />
              </Col>
              <Col>
                <Field
                  required
                  id="vehicleType"
                  name="vehicleType"
                  label="Vehicle Type"
                  type="text"
                  component={FormikInputFieldV2}
                  placeholder="Enter Vehicle Type"
                />
              </Col>
            </Row>
            <Row>
              <Col sm={6}>
                <Field
                  required
                  id="deliveryRate"
                  name="deliveryRate"
                  label="Delivery Rate"
                  type="number"
                  component={FormikInputFieldV2}
                  placeholder="Enter Delivery Rate"
                />
              </Col>
              <Col sm={6}>
                <Field
                  required
                  id="validity"
                  name="validity"
                  label="Validity"
                  type="date"
                  component={FormikInputFieldV2}
                  placeholder="Select Validity"
                />
              </Col>
            </Row>
            <Row>
              <Col sm={6}>
                <Row>
                  <Col sm={10}>
                    <div className="text-dark text-nowrap fw-medium mb-2">
                      Status <span className="text-danger">*</span>
                    </div>
                    <div className="text-gray">
                      Manage the availability of this rate for internal use and
                      future orders.
                    </div>
                  </Col>
                  <Col sm={2}>
                    <Field name="status">
                      {({ field, form }) => visibilityToggle({ field, form })}
                    </Field>
                  </Col>
                </Row>
              </Col>
            </Row>
            <div className="text-end mt-3 mb-1">
              <Button
                outline
                onClick={onDiscard}
                color="secondary"
                type="button"
                size="sm"
                className="me-2 mw-100"
              >
                Discard
              </Button>
              <LoadingButton
                className="mw-100 loading-button"
                loading={isSubmitting}
                disabled={!isValid || !dirty || isSubmitting}
                color="warning"
                size="sm"
                type="submit"
              >
                Save
              </LoadingButton>
            </div>
          </CardWithTitleAndButton>
        </Form>
      )}
    />
  );
};

export default CreateRate;
